import React from 'react';
import { motion } from 'framer-motion';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { CheckCircle2, ExternalLink, TrendingUp } from 'lucide-react';

const PhaseSection = ({
  id,
  phase,
  title,
  subtitle,
  description,
  features,
  metrics,
  imageUrl,
  reverse = false,
  externalLink = null,
  showTractionBadge = false
}) => {
  return (
    <section id={id} className="py-16 lg:py-24">
      <div className="container">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
          reverse ? 'lg:grid-flow-dense' : ''
        }`}>
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: reverse ? 20 : -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={reverse ? 'lg:col-start-2' : ''}
          >
            <Badge variant="outline" className="mb-4">
              {phase}
            </Badge>
            {showTractionBadge && (
              <Badge className="mb-3 bg-accent/15 text-accent border-accent/30 hover:bg-accent/20">
                <TrendingUp className="h-3 w-3 mr-2" />
                Industry Leaders Committed
              </Badge>
            )}
            <h2 className="text-3xl sm:text-4xl font-semibold mb-3">{title}</h2>
            <p className="text-lg text-muted-foreground mb-6">{subtitle}</p>
            <p className="text-base text-muted-foreground leading-relaxed mb-6">
              {description}
            </p>

            {/* External Link if provided */}
            {externalLink && (
              <div className="mb-8">
                <Button
                  asChild
                  variant="outline"
                  className="gap-2"
                >
                  <a
                    href={externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Learn More About The Room
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            )}

            {/* Features List */}
            <div className="space-y-3 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>

            {/* Metrics */}
            <Card className="p-6 bg-secondary/50">
              <h3 className="text-sm uppercase tracking-[0.22em] text-muted-foreground mb-4">
                Key Metrics
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {metrics.map((metric, index) => (
                  <div key={index}>
                    <p className="text-2xl font-bold tabular-nums mb-1">{metric.value}</p>
                    <p className="text-xs text-muted-foreground">{metric.label}</p>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: reverse ? -20 : 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className={reverse ? 'lg:col-start-1 lg:row-start-1' : ''}
          >
            <div className="relative aspect-[4/3] rounded-lg overflow-hidden shadow-md">
              <img
                src={imageUrl}
                alt={title}
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PhaseSection;
